import { useEffect, useMemo, useRef, useState } from "react";
import {
  Grid,
  Card,
  CardContent,
  Button,
  ButtonGroup,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import dayjs, { Dayjs } from "dayjs";
import DatePickerElement from "components/DatePickerElement";
import { SelectField } from "components/@extended/SelectField";
import { IDropdownModel } from "common/models/IDropdownModel";
import {
  IEntryExitDashboardFilter,
  IEntryExitDashboardFilterSchema,
  IDropdownOption,
} from "../api/types";
import { GetMemberTypeValues } from "features/memberType/api/hooks";
import { GetInstituteValues } from "features/institute/api/hooks";
import { GetDepartmentValuesByInstitute } from "features/department/api/hooks";
import { GetProgramValues } from "features/program/api/hooks";

interface FilterBarProps {
  filter: IEntryExitDashboardFilter;
  onFilterChange: (newFilter: IEntryExitDashboardFilter) => void;
  onReset: () => void;
}

export default function FilterBar({
  filter,
  onFilterChange,
  onReset,
}: FilterBarProps) {
  const methods = useForm<IEntryExitDashboardFilter>({
    mode: "all",
    resolver: zodResolver(IEntryExitDashboardFilterSchema),
    defaultValues: filter,
  });

  const {
    reset,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { isSubmitting },
  } = methods;

  const instituteID = watch("instituteID");
  const departmentID = watch("departmentID");
  const watchFromDate = watch("fromDate");
  const watchToDate = watch("toDate");
  useEffect(() => {
    if (watchFromDate && watchToDate) {
      const from = dayjs(watchFromDate);
      const to = dayjs(watchToDate);
      if (from.isValid() && to.isValid() && from.isAfter(to, "day")) {
        setValue("toDate", watchFromDate);
      }
    }
  }, [watchFromDate, watchToDate, setValue]);
  // Dropdown data options
  const { data: memberTypeQuery } = GetMemberTypeValues();
  const [memberTypeOptions, setMemberTypeOptions] = useState<IDropdownModel[]>(
    []
  );
  useEffect(() => {
    setMemberTypeOptions(memberTypeQuery?.apiData || []);
  }, [memberTypeQuery]);

  const { data: instituteQuery } = GetInstituteValues();
  const [instituteOptions, setInstituteOptions] = useState<IDropdownModel[]>([]);
  useEffect(() => {
    setInstituteOptions(instituteQuery?.apiData || []);
  }, [instituteQuery]);

  const { data: departmentQuery } = GetDepartmentValuesByInstitute(
    instituteID || undefined
  );
  const [departmentOptions, setDepartmentOptions] = useState<IDropdownModel[]>(
    []
  );
  useEffect(() => {
    setDepartmentOptions(departmentQuery?.apiData || []);
  }, [departmentQuery]);

  const { data: programQuery } = GetProgramValues();
  const [programOptions, setProgramOptions] = useState<IDropdownModel[]>([]);
  useEffect(() => {
    setProgramOptions(programQuery?.apiData || []);
  }, [programQuery]);

  // Cascading reset
  const prevInstituteID = useRef(instituteID);
  useEffect(() => {
    if (prevInstituteID.current !== instituteID) {
      prevInstituteID.current = instituteID;
      setValue("departmentID", null);
      setValue("programID", null);
    }
  }, [instituteID, setValue]);

  const prevDepartmentID = useRef(departmentID);
  useEffect(() => {
    if (prevDepartmentID.current !== departmentID) {
      prevDepartmentID.current = departmentID;
      setValue("programID", null);
    }
  }, [departmentID, setValue]);

  // Cascading program filtering by department
  const filteredProgramOptions = useMemo(() => {
    if (!programOptions.length) return [];
    if (!departmentID) return programOptions;
    return (programOptions as IDropdownOption[]).filter(
      (p) => p.departmentID === Number(departmentID)
    ) as IDropdownModel[];
  }, [programOptions, departmentID]);

  // Sync external filter with form
  useEffect(() => {
    reset(filter);
  }, [filter, reset]);

  const onSubmit = handleSubmit((data) => {
    onFilterChange({
      fromDate: data.fromDate
        ? dayjs(data.fromDate).format("YYYY-MM-DD")
        : null,
      toDate: data.toDate ? dayjs(data.toDate).format("YYYY-MM-DD") : null,
      memberTypeID: data.memberTypeID ?? null,
      instituteID: data.instituteID ?? null,
      departmentID: data.departmentID ?? null,
      programID: data.programID ?? null,
    });
  });

  const handleClear = () => {
    const today = dayjs().format("YYYY-MM-DD");
    const cleared: IEntryExitDashboardFilter = {
      fromDate: today,
      toDate: today,
      memberTypeID: null,
      instituteID: null,
      departmentID: null,
      programID: null,
    };
    reset(cleared);
    onReset();
  };

  const handlePreset = (
    preset: "today" | "yesterday" | "last7" | "thisMonth"
  ) => {
    let start: Dayjs = dayjs();
    let end: Dayjs = dayjs();

    if (preset === "today") {
      start = dayjs();
      end = dayjs();
    } else if (preset === "yesterday") {
      start = dayjs().subtract(1, "day");
      end = dayjs().subtract(1, "day");
    } else if (preset === "last7") {
      start = dayjs().subtract(6, "day");
      end = dayjs();
    } else if (preset === "thisMonth") {
      start = dayjs().startOf("month");
      end = dayjs();
    }

    const fromDateStr = start.format("YYYY-MM-DD");
    const toDateStr = end.format("YYYY-MM-DD");

    setValue("fromDate", fromDateStr);
    setValue("toDate", toDateStr);

    const currentValues = methods.getValues();
    onFilterChange({
      ...currentValues,
      fromDate: fromDateStr,
      toDate: toDateStr,
    });
  };

  const isTodayActive = Boolean(
    watchFromDate &&
    watchToDate &&
    dayjs(watchFromDate).isSame(dayjs(), "day") &&
    dayjs(watchToDate).isSame(dayjs(), "day")
  );
  const isYesterdayActive = Boolean(
    watchFromDate &&
    watchToDate &&
    dayjs(watchFromDate).isSame(dayjs().subtract(1, "day"), "day") &&
    dayjs(watchToDate).isSame(dayjs().subtract(1, "day"), "day")
  );
  const isLast7Active = Boolean(
    watchFromDate &&
    watchToDate &&
    dayjs(watchFromDate).isSame(dayjs().subtract(6, "day"), "day") &&
    dayjs(watchToDate).isSame(dayjs(), "day")
  );
  const isThisMonthActive = Boolean(
    watchFromDate &&
    watchToDate &&
    dayjs(watchFromDate).isSame(dayjs().startOf("month"), "day") &&
    dayjs(watchToDate).isSame(dayjs(), "day")
  );

  return (
    <Box component="form" onSubmit={onSubmit}>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h4" component={"h3"} marginBlockEnd={2}>
            Visitor Filters
            <Divider sx={{ my: 1 }} />
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <DatePickerElement
                control={control}
                name="fromDate"
                label="From Date"
                format="DD-MM-YYYY"
                maxDate={
                  watchToDate && dayjs(watchToDate).isValid()
                    ? dayjs(watchToDate)
                    : undefined
                }
                sx={{ width: "100%" }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={3}>
              <DatePickerElement
                control={control}
                name="toDate"
                label="To Date"
                format="DD-MM-YYYY"
                minDate={
                  watchFromDate && dayjs(watchFromDate).isValid()
                    ? dayjs(watchFromDate)
                    : undefined
                }
                sx={{ width: "100%" }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={3}>
              <SelectField
                options={memberTypeOptions}
                control={control}
                name="memberTypeID"
                label="Member Type"
                placeholder="Select Member Type"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={3}>
              <SelectField
                options={instituteOptions}
                control={control}
                name="instituteID"
                label="Institute"
                placeholder="Select Institute"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={3}>
              <SelectField
                options={departmentOptions}
                control={control}
                name="departmentID"
                label="Department"
                placeholder="Select Department"
                disabled={!instituteID}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4} lg={3}>
              <SelectField
                options={filteredProgramOptions}
                control={control}
                name="programID"
                label="Program"
                placeholder="Select Program"
                disabled={!departmentID}
              />
            </Grid>
          </Grid>

          <Grid
            container
            spacing={1.5}
            sx={{ mt: 0.5 }}
            alignItems="center"
            justifyContent="space-between"
          >
            <Grid item xs={12} sm="auto" sx={{ display: "flex", gap: 1 }}>
              <LoadingButton
                variant="contained"
                sx={{ flexGrow: { xs: 1, sm: 0 } }}
                type="submit"
                loading={isSubmitting}
                disabled={isSubmitting}
              >
                Search
              </LoadingButton>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleClear}
                sx={{ flexGrow: { xs: 1, sm: 0 } }}
                disabled={isSubmitting}
              >
                Clear
              </Button>
            </Grid>

            <Grid item xs={12} sm="auto">
              <ButtonGroup
                size="medium"
                variant="outlined"
                color="primary"
                sx={{
                  width: { xs: "100%", md: "auto" },
                  display: "flex",
                  flexWrap: "wrap",
                  "& .MuiButton-root": {
                    flex: { xs: "1 1 45%", sm: "auto" },
                  },
                }}
              >
                <Button
                  onClick={() => handlePreset("today")}
                  variant={isTodayActive ? "contained" : "outlined"}
                >
                  Today
                </Button>
                <Button
                  onClick={() => handlePreset("yesterday")}
                  variant={isYesterdayActive ? "contained" : "outlined"}
                >
                  Yesterday
                </Button>
                <Button
                  onClick={() => handlePreset("last7")}
                  variant={isLast7Active ? "contained" : "outlined"}
                >
                  Last 7 Days
                </Button>
                <Button
                  onClick={() => handlePreset("thisMonth")}
                  variant={isThisMonthActive ? "contained" : "outlined"}
                >
                  This Month
                </Button>
              </ButtonGroup>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
}
